import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { DatePicker } from "@/components/DatePicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

const EMPTY = {
  room_id: "", guest_name: "", guest_phone: "", guest_email: "",
  check_in: "", check_out: "", num_guests: 1, notes: "", status: "confirmed",
  channel: "walk_in", contactless: false,
};

export default function BookingForm({ open, onOpenChange, rooms, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const { fmt } = useCurrency();

  useEffect(() => {
    if (open) setForm(EMPTY);
  }, [open]);

  const room = rooms.find((r) => r.id === form.room_id);
  const nights =
    form.check_in && form.check_out
      ? Math.max(0, Math.round((new Date(form.check_out) - new Date(form.check_in)) / 86400000))
      : 0;
  const total = room && nights > 0 ? room.rate * nights : 0;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/bookings", { ...form, num_guests: parseInt(form.num_guests, 10) });
      toast.success("Booking created");
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="overflow-y-auto sm:max-w-md" data-testid="booking-form-sheet">
        <SheetHeader>
          <SheetTitle className="font-heading text-xl">New booking</SheetTitle>
        </SheetHeader>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div className="space-y-1.5">
            <Label>Room</Label>
            <Select value={form.room_id} onValueChange={(v) => setForm({ ...form, room_id: v })} required>
              <SelectTrigger data-testid="booking-form-room"><SelectValue placeholder="Select a room" /></SelectTrigger>
              <SelectContent>
                {rooms.map((r) => (
                  <SelectItem key={r.id} value={r.id}>
                    Room {r.name} · {r.type} · {fmt(r.rate)}/night
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Check-in</Label>
              <DatePicker testId="booking-form-check-in" value={form.check_in} onChange={(v) => setForm({ ...form, check_in: v })} placeholder="Check-in date" />
            </div>
            <div className="space-y-1.5">
              <Label>Check-out</Label>
              <DatePicker testId="booking-form-check-out" value={form.check_out} onChange={(v) => setForm({ ...form, check_out: v })} placeholder="Check-out date" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Guest name</Label>
            <Input data-testid="booking-form-guest-name" value={form.guest_name} onChange={(e) => setForm({ ...form, guest_name: e.target.value })} required />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Phone</Label>
              <Input data-testid="booking-form-guest-phone" value={form.guest_phone} onChange={(e) => setForm({ ...form, guest_phone: e.target.value })} placeholder="+260 …" />
            </div>
            <div className="space-y-1.5">
              <Label>Guests</Label>
              <Input type="number" min="1" data-testid="booking-form-num-guests" value={form.num_guests} onChange={(e) => setForm({ ...form, num_guests: e.target.value })} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Guest email (optional)</Label>
            <Input type="email" data-testid="booking-form-guest-email" value={form.guest_email} onChange={(e) => setForm({ ...form, guest_email: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label>Booking channel</Label>
            <Select value={form.channel} onValueChange={(v) => setForm({ ...form, channel: v })}>
              <SelectTrigger data-testid="booking-form-channel"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="walk_in">Walk-in</SelectItem>
                <SelectItem value="phone">Phone</SelectItem>
                <SelectItem value="website">Website</SelectItem>
                <SelectItem value="easystay_app">EasyStay App</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-between border border-border px-4 py-3" data-testid="booking-form-contactless-row">
            <div>
              <div className="text-sm font-medium">Contactless check-in</div>
              <div className="text-xs text-muted-foreground">Guest receives a digital key by text via the EasyStay app.</div>
            </div>
            <Switch checked={form.contactless} onCheckedChange={(v) => setForm({ ...form, contactless: v })} data-testid="booking-form-contactless" />
          </div>
          <div className="space-y-1.5">
            <Label>Notes (optional)</Label>
            <Textarea data-testid="booking-form-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
          </div>
          {total > 0 && (
            <div className="flex items-center justify-between border border-border bg-secondary/50 px-4 py-3" data-testid="booking-form-total">
              <span className="text-sm text-muted-foreground">{nights} night{nights > 1 ? "s" : ""} × {fmt(room.rate)}</span>
              <span className="font-heading text-xl font-bold">{fmt(total)}</span>
            </div>
          )}
          <Button type="submit" disabled={saving || !form.room_id || !form.check_in || !form.check_out || nights < 1}
            data-testid="booking-form-submit" className="w-full transition-colors">
            {saving ? "Creating…" : "Create booking"}
          </Button>
        </form>
      </SheetContent>
    </Sheet>
  );
}
